export type AlternativeCategory = 'cloud-hosting' | 'model-infra' | 'agent-cloud';

interface AlternativeSeed {
  slug: string;
  competitor: string;
  category: AlternativeCategory;
  url: string;
  summary: string;
  strengths: string[];
  chooseThem: string;
  appNzAngle: string;
  migration: string;
  specialNote?: string;
}

export interface Alternative {
  slug: string;
  title: string;
  competitor: string;
  category: AlternativeCategory;
  categoryLabel: string;
  url: string;
  excerpt: string;
  readTime: string;
  summary: string;
  strengths: string[];
  chooseThem: string;
  appNzAngle: string;
  migration: string;
  body: string;
}

export const alternativeCategoryLabels: Record<AlternativeCategory, string> = {
  'cloud-hosting': 'Cloud hosting',
  'model-infra': 'AI model infrastructure',
  'agent-cloud': 'AI agent cloud',
};

const appNzCore = [
  'cloud coding agents that work on repos and return durable diffs',
  'static sites, server deploys, private container builds, domains, logs, queues, and secrets',
  'one OpenAI-compatible gateway for text, image, video, audio, embeddings, search, and 3D',
  'scale-to-zero GPU hosting for Cog and Comfy workloads',
  'one credit balance and API surface across the product',
];

function list(items: string[]): string {
  return items.map((item) => `- ${item}`).join('\n');
}

function articleBody(seed: AlternativeSeed): string {
  const special = seed.specialNote
    ? `\n\n## A note on ${seed.competitor}\n\n${seed.specialNote}\n`
    : '';

  return `
## ${seed.competitor} Alternative: app.nz

${seed.summary} See [${seed.competitor}](${seed.url}).

## Where ${seed.competitor} is strong

${list(seed.strengths)}

## Where app.nz is different

app.nz covers:

${list(appNzCore)}

${seed.appNzAngle}

## Choose ${seed.competitor} when

${seed.chooseThem}

## Choose app.nz when

app.nz runs the whole loop on one account: an agent edits code, the build deploys, model calls go through the gateway, and GPU endpoints run on demand.

## Migration path

${seed.migration}

## Bottom line

app.nz hosts the app, routes the models, and runs the GPU or worker infrastructure behind them.${special}
`;
}

const seeds: AlternativeSeed[] = [
  {
    slug: 'vercel',
    competitor: 'Vercel',
    category: 'cloud-hosting',
    url: 'https://vercel.com',
    summary: 'Vercel hosts frontend and full-stack Next.js apps with preview deployments and edge rendering.',
    strengths: ['Next.js frontend workflow.', 'Preview and branch deployments.', 'Framework integrations and web-performance tooling.'],
    chooseThem: 'Choose Vercel to deploy a frontend or full-stack Next.js app with preview deployments and edge delivery.',
    appNzAngle: 'app.nz fits when the frontend is one part of the system: agents, model routing, queues, and scale-to-zero GPUs.',
    migration: 'Move static output to app.nz sites first, then API containers or GPU-backed model endpoints.',
  },
  {
    slug: 'netlify',
    competitor: 'Netlify',
    category: 'cloud-hosting',
    url: 'https://www.netlify.com',
    summary: 'Netlify hosts Jamstack sites with deploy previews, forms, functions, and edge hosting.',
    strengths: ['Static and Jamstack deploys.', 'Previews and branch collaboration.', 'Built-in forms and functions.'],
    chooseThem: 'Choose Netlify when the site itself is the main product and you want a Jamstack workflow.',
    appNzAngle: 'app.nz covers a wider loop: coding agents, model gateway, GPU model hosting, container builds, datasets, notebooks, and deploys.',
    migration: 'Deploy your built frontend as an app.nz site, and keep backend and model work there.',
  },
  {
    slug: 'cloudflare-pages-workers',
    competitor: 'Cloudflare Pages and Workers',
    category: 'cloud-hosting',
    url: 'https://www.cloudflare.com/developer-platform/',
    summary: 'Cloudflare runs a global edge network with Pages, Workers, storage, queues, and edge compute.',
    strengths: ['Large global edge network.', 'Workers run low-latency edge logic.', 'Storage, queues, and security features.'],
    chooseThem: 'Choose Cloudflare when you need edge-first runtime behavior close to users.',
    appNzAngle: 'app.nz does not replace the Cloudflare network. It covers AI app building: agents, model routing, GPU inference, and deploys.',
    migration: 'Keep Cloudflare for DNS or edge security, and move app builds, model gateway, agents, or GPU workloads to app.nz.',
  },
  {
    slug: 'heroku',
    competitor: 'Heroku',
    category: 'cloud-hosting',
    url: 'https://www.heroku.com',
    summary: 'Heroku is a PaaS with git-driven deploys, dynos, add-ons, and logs.',
    strengths: ['App-centric deploy model.', 'Add-on ecosystem and operational defaults.', 'Simpler than managing raw infrastructure.'],
    chooseThem: 'Choose Heroku for a classic PaaS running web processes, workers, and add-ons.',
    appNzAngle: 'app.nz hosts the app, runs agents on the repo, routes model calls, and serves GPU endpoints.',
    migration: 'Containerize the app or deploy static output, then move model calls to the app.nz gateway and background work to app.nz queues.',
  },
  {
    slug: 'railway',
    competitor: 'Railway',
    category: 'cloud-hosting',
    url: 'https://railway.app',
    summary: 'Railway deploys apps and databases with project-based infrastructure.',
    strengths: ['Fast setup for apps and databases.', 'Ergonomics for small teams.', 'Prototype to hosted service in one project.'],
    chooseThem: 'Choose Railway for a general-purpose app host when your infrastructure is services and databases.',
    appNzAngle: 'app.nz fits AI-native apps where coding agents, model routing, GPU inference, and deploys work together.',
    migration: 'Start with static hosting, gateway calls, or agent repo work, then move containers once deploys work.',
  },
  {
    slug: 'render',
    competitor: 'Render',
    category: 'cloud-hosting',
    url: 'https://render.com',
    summary: 'Render runs web services, static sites, background workers, cron jobs, and managed databases.',
    strengths: ['One service model for web, worker, cron, and static deploys.', 'Suits conventional web apps.', 'Managed databases and basic operations.'],
    chooseThem: 'Choose Render for conventional apps with a clean service model and managed primitives.',
    appNzAngle: 'app.nz adds the AI parts: agents that change code, gateway routes that pick models, and scale-to-zero GPU endpoints.',
    migration: 'Move static sites or containers incrementally; route AI calls through app.nz first.',
  },
  {
    slug: 'fly-io',
    competitor: 'Fly.io',
    category: 'cloud-hosting',
    url: 'https://fly.io',
    summary: 'Fly.io runs containers close to users across regions.',
    strengths: ['Container-first deploys.', 'Global placement.', 'App instances close to users.'],
    chooseThem: 'Choose Fly.io for distributed container placement and low-latency instances across regions.',
    appNzAngle: 'app.nz fits when the AI workflow matters more than regional placement: agents, model gateway, GPU cogs, Comfy workflows, and queues.',
    migration: 'Keep latency-sensitive services on Fly.io, and move AI routing, model hosting, and agent deploys to app.nz.',
  },
  {
    slug: 'digitalocean-app-platform',
    competitor: 'DigitalOcean App Platform',
    category: 'cloud-hosting',
    url: 'https://www.digitalocean.com/products/app-platform',
    summary: 'DigitalOcean offers droplets, managed databases, Kubernetes, object storage, and App Platform.',
    strengths: ['Developer-friendly cloud primitives.', 'Simple virtual machines and managed databases.', 'Broad infrastructure without hyperscaler complexity.'],
    chooseThem: 'Choose DigitalOcean for straightforward infrastructure you operate resource by resource.',
    appNzAngle: 'app.nz works a level above the infrastructure: agent tasks, deploys, model routes, GPU workloads, notebooks, and queues.',
    migration: 'Move the parts that benefit most first: model gateway traffic, agent tasks, static sites, or GPU endpoints.',
  },
  {
    slug: 'aws',
    competitor: 'AWS',
    category: 'cloud-hosting',
    url: 'https://aws.amazon.com',
    summary: 'AWS is the broadest cloud platform: infrastructure choice, enterprise controls, many regions, and a large service catalog.',
    strengths: ['Broad infrastructure and managed-service catalog.', 'Enterprise controls and global availability.', 'Suits teams with platform engineering capacity.'],
    chooseThem: 'Choose AWS for infrastructure breadth, enterprise governance, or low-level control of cloud architecture.',
    appNzAngle: 'app.nz is narrower: agents, deploys, model routing, GPU endpoints, and app hosting without assembling dozens of services.',
    migration: 'Put app.nz in front of model traffic or agent workflows first, then move the deploys or GPU workloads that no longer need custom AWS infrastructure.',
  },
  {
    slug: 'google-cloud',
    competitor: 'Google Cloud',
    category: 'cloud-hosting',
    url: 'https://cloud.google.com',
    summary: 'Google Cloud covers data, Kubernetes, and AI infrastructure, and integrates with Google identity.',
    strengths: ['Data and AI platform services.', 'Mature Kubernetes and infrastructure.', 'Suits teams already on Google Cloud.'],
    chooseThem: 'Choose Google Cloud when you already run on GCP or need its data, AI, and infrastructure services directly.',
    appNzAngle: 'app.nz sits above the cloud: model routing, agents, deploys, GPU hosting, and notebooks are product surfaces.',
    migration: 'Use app.nz for the application and model workflow; keep existing GCP data or enterprise systems where they are.',
  },
  {
    slug: 'azure',
    competitor: 'Microsoft Azure',
    category: 'cloud-hosting',
    url: 'https://azure.microsoft.com',
    summary: 'Azure is an enterprise cloud with Microsoft identity, compliance, and hybrid infrastructure.',
    strengths: ['Microsoft and enterprise ecosystem fit.', 'Broad infrastructure and managed services.', 'Governance for large organizations.'],
    chooseThem: 'Choose Azure when Microsoft identity, procurement, compliance, or existing Azure estates are requirements.',
    appNzAngle: 'app.nz is a smaller AI control plane: coding agents, deploys, model routing, GPU endpoints, and operational primitives.',
    migration: 'Keep enterprise systems on Azure and move AI app workflows, agent tasks, or model calls to app.nz.',
  },
  {
    slug: 'firebase',
    competitor: 'Firebase',
    category: 'cloud-hosting',
    url: 'https://firebase.google.com',
    summary: 'Firebase provides auth, realtime data, functions, hosting, and analytics for web and mobile apps.',
    strengths: ['Mobile and frontend tooling.', 'Integrated auth, database, functions, and hosting.', 'Suits realtime prototypes and consumer apps.'],
    chooseThem: 'Choose Firebase when the app depends on Firebase auth, realtime data, mobile SDKs, or the Google app platform.',
    appNzAngle: 'app.nz covers AI production loops: code agents, model gateway, GPU inference, hosted sites, queues, and notebooks.',
    migration: 'Keep Firebase auth or data, and use app.nz for gateway calls, agent code work, static deploys, or GPU features.',
  },
  {
    slug: 'supabase',
    competitor: 'Supabase',
    category: 'cloud-hosting',
    url: 'https://supabase.com',
    summary: 'Supabase is an open-source Firebase alternative built on Postgres, with auth, storage, realtime, and edge functions.',
    strengths: ['Postgres-centered developer experience.', 'Open source, standard SQL.', 'Integrated auth, storage, realtime, and functions.'],
    chooseThem: 'Choose Supabase when Postgres, auth, and database-backed app development are the main problems.',
    appNzAngle: 'app.nz handles the AI workflow next to the database: agents, model routing, GPU hosting, and app deploys.',
    migration: 'A common split is Supabase for data and app.nz for AI and hosting: route model calls through app.nz and deploy frontends to sites.',
  },
  {
    slug: 'deno-deploy',
    competitor: 'Deno Deploy',
    category: 'cloud-hosting',
    url: 'https://deno.com/deploy',
    summary: 'Deno Deploy runs JavaScript and TypeScript at the edge.',
    strengths: ['TypeScript-first runtime.', 'Edge deployment.', 'Suits teams already using Deno.'],
    chooseThem: 'Choose Deno Deploy when the app is Deno-native and edge JS or TS execution is the main requirement.',
    appNzAngle: 'app.nz hosts static and containerized apps, runs coding agents, routes model calls, and serves GPU inference.',
    migration: 'Move the frontend or AI-facing service to app.nz when you need model routing or deploy automation.',
  },
  {
    slug: 'github-pages',
    competitor: 'GitHub Pages',
    category: 'cloud-hosting',
    url: 'https://pages.github.com',
    summary: 'GitHub Pages publishes static content from a GitHub repository.',
    strengths: ['Static publishing from a repo.', 'Fits docs, project pages, and open-source sites.', 'Low operational overhead.'],
    chooseThem: 'Choose GitHub Pages when you only need static public pages and no app hosting or AI infrastructure.',
    appNzAngle: 'app.nz keeps static hosting and adds agents, model gateway, GPU workloads, private builds, queues, domains, and logs.',
    migration: 'Build the static output and deploy it to app.nz sites when the page should sit next to agents, model calls, and analytics.',
  },
  {
    slug: 'gitlab-pages',
    competitor: 'GitLab Pages',
    category: 'cloud-hosting',
    url: 'https://docs.gitlab.com/user/project/pages/',
    summary: 'GitLab Pages publishes static pages from a GitLab CI/CD pipeline.',
    strengths: ['Fits GitLab-centered teams.', 'Runs from GitLab CI/CD.', 'Static site publishing.'],
    chooseThem: 'Choose GitLab Pages when the team is on GitLab and needs static publishing from its pipelines.',
    appNzAngle: 'app.nz goes beyond static publishing: agents that edit code, model gateway calls, GPU endpoints, deploys, and notebooks.',
    migration: 'Keep GitLab as source control, and deploy built output or AI workloads to app.nz.',
  },
  {
    slug: 'platform-sh',
    competitor: 'Platform.sh',
    category: 'cloud-hosting',
    url: 'https://platform.sh',
    summary: 'Platform.sh runs complex web apps with preview environments and managed infrastructure.',
    strengths: ['Preview and environment workflows.', 'Suits complex application stacks.', 'Platform controls for larger teams.'],
    chooseThem: 'Choose Platform.sh when you need environment management for conventional application stacks.',
    appNzAngle: 'app.nz is AI-native: agents, model routing, GPU hosting, and deploy workflows are core, not add-ons.',
    migration: 'Move AI-facing services or frontend deploys to app.nz first; keep complex legacy environments on Platform.sh.',
  },
  {
    slug: 'koyeb',
    competitor: 'Koyeb',
    category: 'cloud-hosting',
    url: 'https://www.koyeb.com',
    summary: 'Koyeb is a serverless platform for containers, web apps, APIs, workers, and AI workloads.',
    strengths: ['Container and app deploys.', 'APIs, workers, and serverless-style services.', 'Less infrastructure management.'],
    chooseThem: 'Choose Koyeb when containerized services and serverless deployment are the main requirements.',
    appNzAngle: 'app.nz fits when the deploy is one part of an AI system with coding agents, model routing, GPU endpoints, and queues.',
    migration: 'Container services can move gradually; gateway usage and agent code tasks are usually the easiest first step.',
  },
  {
    slug: 'northflank',
    competitor: 'Northflank',
    category: 'cloud-hosting',
    url: 'https://northflank.com',
    summary: 'Northflank deploys services, jobs, databases, and pipelines with container and environment workflows.',
    strengths: ['Container deploys and pipeline model.', 'Service, job, and database primitives.', 'More control than static hosting.'],
    chooseThem: 'Choose Northflank when your main platform need is container orchestration, jobs, environments, and databases.',
    appNzAngle: 'app.nz adds AI surfaces: agents on repos, model gateway routing, GPU cogs, Comfy workflows, and app deploys.',
    migration: 'Move AI workloads and static deploys first; containers can follow.',
  },
  {
    slug: 'qovery',
    competitor: 'Qovery',
    category: 'cloud-hosting',
    url: 'https://www.qovery.com',
    summary: 'Qovery is a control plane for deploying applications onto your own cloud or Kubernetes infrastructure.',
    strengths: ['Platform layer over existing cloud infrastructure.', 'Kubernetes-backed application delivery.', 'Standardized deployments across teams.'],
    chooseThem: 'Choose Qovery to run on your own cloud account with a simpler deployment layer for developers.',
    appNzAngle: 'app.nz goes past deployment: agents, model routing, GPU hosting, app hosting, and queues.',
    migration: 'Use app.nz for new AI apps and agent workflows; existing Kubernetes services can stay on Qovery.',
  },
  {
    slug: 'coolify',
    competitor: 'Coolify',
    category: 'cloud-hosting',
    url: 'https://coolify.io',
    summary: 'Coolify is an open-source, self-hosted platform for deploying apps, databases, and services on your own servers.',
    strengths: ['Open source and self-hosted.', 'Control over your own servers.', 'App, database, and service deploys without a platform team.'],
    chooseThem: 'Choose Coolify when self-hosting and control are more important than managed AI-specific workflows.',
    appNzAngle: 'app.nz trades self-hosting control for one product: coding agents, model gateway, GPU endpoints, sites, builds, queues, and billing.',
    migration: 'Keep self-hosted services on Coolify; use app.nz for the AI gateway, agents, GPU workloads, or hosted sites.',
  },
  {
    slug: 'dokku',
    competitor: 'Dokku',
    category: 'cloud-hosting',
    url: 'https://dokku.com',
    summary: 'Dokku is a small open-source PaaS with Heroku-style git push deploys to your own server.',
    strengths: ['Small, self-hosted, easy to read.', 'Heroku-style deploys on one VM.', 'Fewer abstractions than larger platforms.'],
    chooseThem: 'Choose Dokku when you want to own the server and keep deployment mechanics simple.',
    appNzAngle: 'app.nz lets the platform own more: agents, model routing, GPU hosting, static and server deploys, queues, and logs.',
    migration: 'Move the apps that need AI gateway, GPU, or agents first; simple services can stay on Dokku.',
  },
  {
    slug: 'caprover',
    competitor: 'CapRover',
    category: 'cloud-hosting',
    url: 'https://caprover.com',
    summary: 'CapRover is a self-hosted PaaS for web apps, databases, and one-click app templates on your own server.',
    strengths: ['Self-hosted platform with a visual control plane.', 'Suits teams that operate their own server.', 'Simple web app and database deploys.'],
    chooseThem: 'Choose CapRover when self-hosting is the goal and your workloads fit a straightforward app platform.',
    appNzAngle: 'app.nz is the managed alternative: coding agents, deploys, model gateway, GPU hosting, queues, and app hosting.',
    migration: 'Use app.nz for AI-facing services, frontends, or GPU workloads; keep internal apps on CapRover.',
  },
  {
    slug: 'hetzner',
    competitor: 'Hetzner',
    category: 'cloud-hosting',
    url: 'https://www.hetzner.com',
    summary: 'Hetzner sells cost-effective virtual and dedicated servers.',
    strengths: ['Value pricing for virtual and dedicated servers.', 'Suits teams that manage machines.', 'Predictable server workloads.'],
    chooseThem: 'Choose Hetzner when you want raw server value and are comfortable assembling the platform yourself.',
    appNzAngle: 'app.nz can use Hetzner behind the scenes and exposes agents, deploys, model routing, GPU hosting, queues, and notebooks instead of raw servers.',
    migration: 'Keep cost-sensitive infrastructure on Hetzner; move app and AI orchestration to app.nz when developer speed matters.',
  },
  {
    slug: 'vultr',
    competitor: 'Vultr',
    category: 'cloud-hosting',
    url: 'https://www.vultr.com',
    summary: 'Vultr offers cloud compute, bare metal, Kubernetes, storage, and GPUs.',
    strengths: ['Broad infrastructure primitives.', 'Direct control over servers and regions.', 'Compute for conventional hosting.'],
    chooseThem: 'Choose Vultr when you need infrastructure building blocks and are ready to operate the stack yourself.',
    appNzAngle: 'app.nz is the productized alternative: agents, deploys, model routes, GPU cogs, Comfy workflows, queues, and logs.',
    migration: 'Move the AI product layer first; raw infrastructure can stay on Vultr.',
  },
  {
    slug: 'openrouter',
    competitor: 'OpenRouter',
    category: 'model-infra',
    url: 'https://openrouter.ai',
    summary: 'OpenRouter is a model router with one API for many language models.',
    strengths: ['Broad model catalog behind an OpenAI-compatible API.', 'Comparing and routing language models.', 'Suits apps whose main need is chat model access.'],
    chooseThem: 'Choose OpenRouter when you need a model router and hosting already lives elsewhere.',
    appNzAngle: 'app.nz has a model gateway too, next to coding agents, deploys, GPU model hosting, queues, datasets, and app hosting.',
    migration: 'OpenAI-compatible clients move by changing the base URL and key, then choosing app.nz auto routes or pinned provider routes.',
  },
  {
    slug: 'openai',
    competitor: 'OpenAI API',
    category: 'model-infra',
    url: 'https://platform.openai.com',
    summary: 'The OpenAI API is the default starting point for teams using frontier models and the OpenAI SDK.',
    strengths: ['Frontier models and a widely used SDK shape.', 'Large ecosystem of examples and client libraries.', 'Suits OpenAI-only access.'],
    chooseThem: 'Choose the OpenAI API directly for vendor access without cross-provider routing or app hosting.',
    appNzAngle: 'app.nz keeps the OpenAI-compatible shape, routes across providers, and adds app deploys, coding agents, and GPU endpoints.',
    migration: 'Switch the base URL and API key, then use app.nz model ids or auto routes.',
  },
  {
    slug: 'anthropic',
    competitor: 'Anthropic API',
    category: 'model-infra',
    url: 'https://www.anthropic.com/api',
    summary: 'Anthropic builds the Claude models and serves them directly.',
    strengths: ['Claude model family.', 'Suits teams standardizing on Anthropic.', 'Direct vendor relationship and provider-specific features.'],
    chooseThem: 'Choose Anthropic directly when Claude is your primary model family and vendor-specific API behavior is important.',
    appNzAngle: 'app.nz routes Anthropic alongside other providers and connects those calls to agents, hosting, deploys, and billing.',
    migration: 'Use app.nz for OpenAI-compatible routed calls or keep direct Anthropic usage where provider-specific features matter.',
  },
  {
    slug: 'together-ai',
    competitor: 'Together AI',
    category: 'model-infra',
    url: 'https://www.together.ai',
    summary: 'Together AI serves open-weight models and supports fine-tuning.',
    strengths: ['Open-model infrastructure.', 'Llama, DeepSeek, Mistral, and similar families.', 'Open-weight inference workflows.'],
    chooseThem: 'Choose Together AI when open model infrastructure is the center of your stack.',
    appNzAngle: 'app.nz routes to open and closed models, hosts apps, runs agents, and deploys GPU endpoints.',
    migration: 'Keep specialized open-model workflows where they are strongest; route application traffic through app.nz.',
  },
  {
    slug: 'replicate',
    competitor: 'Replicate',
    category: 'model-infra',
    url: 'https://replicate.com',
    summary: 'Replicate runs machine-learning models as APIs, including community image, video, and Cog-style models.',
    strengths: ['Large ecosystem of model APIs.', 'Quick way to try community models.', 'Cog conventions for model packaging.'],
    chooseThem: 'Choose Replicate for its model ecosystem and hosted model APIs.',
    appNzAngle: 'app.nz supports Cog-style GPU hosting next to deploys, agents, model gateway routing, Comfy workflows, and queues.',
    migration: 'Move custom Cog containers to app.nz when the model endpoint should live with the app platform; keep Replicate where its marketplace is fastest.',
  },
  {
    slug: 'fal-ai',
    competitor: 'fal.ai',
    category: 'model-infra',
    url: 'https://fal.ai',
    summary: 'fal.ai provides fast image, video, and media-generation APIs.',
    strengths: ['Image, video, and media model APIs.', 'Developer experience for creative workloads.', 'Model catalog for generation tasks.'],
    chooseThem: 'Choose fal.ai for fal-hosted media models and direct access to that ecosystem.',
    appNzAngle: 'app.nz routes media models and adds app hosting, agents, GPU cogs, and Comfy workflows.',
    migration: 'Use app.nz gateway routes for app-level media calls, and keep direct fal usage for specific fal endpoints.',
  },
  {
    slug: 'runpod',
    competitor: 'RunPod',
    category: 'model-infra',
    url: 'https://www.runpod.io',
    summary: 'RunPod is a GPU cloud for pods and serverless GPU workloads.',
    strengths: ['GPU pods and serverless GPU execution.', 'Suits teams that know their container and GPU needs.', 'GPU marketplace and operational model.'],
    chooseThem: 'Choose RunPod to manage GPU endpoints yourself with provider-level control.',
    appNzAngle: 'app.nz can use RunPod-style GPU capacity and exposes cogs, Comfy workflows, routing, idle reaping, metering, and app integration.',
    migration: 'Move model images to app.nz cogs or Comfy deployments for scale-to-zero GPU behavior.',
  },
  {
    slug: 'modal',
    competitor: 'Modal',
    category: 'model-infra',
    url: 'https://modal.com',
    summary: 'Modal is a serverless compute platform for Python, ML, batch jobs, and GPU-backed functions.',
    strengths: ['Python-first compute workflow.', 'ML jobs, batch work, and serverless GPU functions.', 'Developer-friendly abstraction over infrastructure.'],
    chooseThem: 'Choose Modal when your workload is primarily Python functions, ML jobs, or serverless compute pipelines.',
    appNzAngle: 'app.nz is the product around the compute: coding agents, deploys, model gateway, hosted apps, GPU endpoints, and billing.',
    migration: 'Keep specialized Python compute on Modal; move product-facing model endpoints, agents, and app deploys to app.nz.',
  },
  {
    slug: 'baseten',
    competitor: 'Baseten',
    category: 'model-infra',
    url: 'https://www.baseten.co',
    summary: 'Baseten deploys, scales, and operates machine-learning inference.',
    strengths: ['Model-serving focus.', 'Suits teams operationalizing ML inference.', 'Useful when inference is the main platform problem.'],
    chooseThem: 'Choose Baseten when production ML inference operations are the central requirement.',
    appNzAngle: 'app.nz includes inference plus app hosting, coding agents, gateway routing, GPU cogs, and deploys.',
    migration: 'Use app.nz for app workflows and gateway calls first; move model endpoints when they should share the control plane.',
  },
  {
    slug: 'hugging-face-spaces',
    competitor: 'Hugging Face Spaces',
    category: 'model-infra',
    url: 'https://huggingface.co/spaces',
    summary: 'Hugging Face Spaces hosts ML demos and apps for the Hugging Face community.',
    strengths: ['Model and community ecosystem.', 'Gradio, Streamlit, and demo apps.', 'Discovery for open ML projects.'],
    chooseThem: 'Choose Hugging Face Spaces when community sharing, demos, and the Hugging Face ecosystem are the primary goals.',
    appNzAngle: 'app.nz targets production AI app workflows: coding agents, deploys, model gateway, GPU hosting, and billing.',
    migration: 'Keep public demos on Spaces if discovery matters; deploy production-facing apps, gateways, or GPU endpoints to app.nz.',
  },
  {
    slug: 'lightning-ai',
    competitor: 'Lightning AI',
    category: 'model-infra',
    url: 'https://lightning.ai',
    summary: 'Lightning AI provides development workspaces, training, and experiments.',
    strengths: ['Environment for AI research and experimentation.', 'GPU workspaces and training workflows.', 'Suits teams already using Lightning.'],
    chooseThem: 'Choose Lightning AI when experimentation, training, and ML workspaces are the main need.',
    appNzAngle: 'app.nz is for shipping the result: agents edit code, apps deploy, models route through a gateway, and GPU inference scales down.',
    migration: 'Use Lightning AI for research-heavy workflows and app.nz for deployed apps, model endpoints, and product infrastructure.',
  },
  {
    slug: 'vast-ai',
    competitor: 'Vast.ai',
    category: 'model-infra',
    url: 'https://vast.ai',
    summary: 'Vast.ai is a GPU marketplace for renting compute by price and availability.',
    strengths: ['Broad GPU marketplace.', 'Optimizing raw GPU cost.', 'Suits users who manage machines themselves.'],
    chooseThem: 'Choose Vast.ai when you want direct GPU rentals and are prepared to manage setup, security, images, and lifecycle yourself.',
    appNzAngle: 'app.nz turns GPU work into product primitives: Cog and Comfy deploys, routing, health checks, idle reaping, metering, and app integration.',
    migration: 'Move recurring inference workloads to app.nz when lifecycle management is more valuable than raw marketplace control.',
  },
  {
    slug: 'fireworks-ai',
    competitor: 'Fireworks AI',
    category: 'model-infra',
    url: 'https://fireworks.ai',
    summary: 'Fireworks AI serves generative models, with a focus on fast inference for open and customizable models.',
    strengths: ['Inference platform for model serving.', 'Open and customizable models.', 'Useful when latency and throughput matter most.'],
    chooseThem: 'Choose Fireworks AI when model-serving performance is the core need and the rest of the platform is handled.',
    appNzAngle: 'app.nz puts model routing inside a larger AI app cloud with deploys, agents, GPU endpoints, queues, and hosting.',
    migration: 'Route product traffic through app.nz for provider flexibility; keep direct Fireworks usage for workloads tuned to that platform.',
  },
  {
    slug: 'groq',
    competitor: 'GroqCloud',
    category: 'model-infra',
    url: 'https://groq.com',
    summary: 'GroqCloud serves supported models with fast inference and a simple API.',
    strengths: ['Fast inference for supported model families.', 'Latency-sensitive text workloads.', 'Simple path to try supported models.'],
    chooseThem: 'Choose GroqCloud when supported model latency is the main requirement.',
    appNzAngle: 'app.nz routes across providers and modalities, hosts the app, and runs coding agents.',
    migration: 'Keep Groq as a direct lane for latency-critical calls, or use fast model routes through app.nz.',
  },
  {
    slug: 'cerebras-cloud',
    competitor: 'Cerebras Cloud',
    category: 'model-infra',
    url: 'https://www.cerebras.ai',
    summary: 'Cerebras Cloud runs high-speed inference on Cerebras hardware.',
    strengths: ['Fast inference for supported workloads.', 'Hardware-backed model serving.', 'Suits teams testing high-throughput text generation.'],
    chooseThem: 'Choose Cerebras Cloud for its supported models and hardware-backed latency.',
    appNzAngle: 'app.nz fits when Cerebras is one of several providers and the product also needs deploys, agents, GPU hosting, and model fallback.',
    migration: 'Use app.nz as the routing layer where provider flexibility matters, and keep direct Cerebras paths for high-speed needs.',
  },
  {
    slug: 'aws-bedrock',
    competitor: 'Amazon Bedrock',
    category: 'model-infra',
    url: 'https://aws.amazon.com/bedrock/',
    summary: 'Amazon Bedrock gives AWS customers managed access to foundation models.',
    strengths: ['Fits AWS-centered organizations.', 'Managed access to multiple model families.', 'Enterprise controls and AWS integration.'],
    chooseThem: 'Choose Bedrock when AWS governance, procurement, and integration are the deciding factors.',
    appNzAngle: 'app.nz is lighter: one gateway, coding agents, deploys, GPU endpoints, and app hosting.',
    migration: 'Keep regulated or AWS-native model calls in Bedrock; use app.nz for cross-provider routing and app infrastructure.',
  },
  {
    slug: 'vertex-ai',
    competitor: 'Vertex AI',
    category: 'model-infra',
    url: 'https://cloud.google.com/vertex-ai',
    summary: "Vertex AI is Google Cloud's managed platform for models, training, and deployment.",
    strengths: ['Fits Google Cloud teams.', 'Managed AI and ML platform.', 'Enterprise data and model workflows inside GCP.'],
    chooseThem: 'Choose Vertex AI when AI work is tied to Google Cloud data, governance, and ML workflows.',
    appNzAngle: 'app.nz is a simpler AI app cloud: gateway, agents, deploys, GPU endpoints, notebooks, and hosted sites.',
    migration: 'Keep GCP-native model and data workflows on Vertex; move application-level routing, deploys, and agents to app.nz.',
  },
  {
    slug: 'codex-infinity',
    competitor: 'Codex Infinity',
    category: 'agent-cloud',
    url: 'https://codex-infinity.com',
    summary: 'Codex Infinity focuses on long-running autonomous coding sessions and Codex-style agent orchestration.',
    strengths: ['Long-running coding agents.', 'Autonomous continuation and goal-driven workflows.', 'For builders pushing agents toward longer horizons.'],
    chooseThem: 'Choose Codex Infinity when the core job is autonomous coding-agent orchestration and long-running agent behavior.',
    appNzAngle: 'app.nz overlaps on agents but is broader: it hosts apps, deploys containers and static sites, routes models, and runs GPU endpoints.',
    migration: 'Use Codex Infinity for long-running agent experiments and app.nz when the result needs hosting, model routing, and GPU inference.',
    specialNote:
      'Codex Infinity focuses on coding agents that keep working over long horizons. The wider Codex Infinity/OpenPath ecosystem also experiments with agent-friendly payment flows, including Solana-oriented work. app.nz is the broader hosted cloud around that style of agent work.',
  },
  {
    slug: 'cursor',
    competitor: 'Cursor',
    category: 'agent-cloud',
    url: 'https://cursor.com',
    summary: 'Cursor is an AI code editor with model-assisted coding in the IDE.',
    strengths: ['Editor-native AI coding.', 'Assistance inside the IDE.', 'Code understanding and edits.'],
    chooseThem: 'Choose Cursor when the IDE is the center of your workflow.',
    appNzAngle: 'app.nz is not an editor. It runs cloud agents, hosts apps, deploys outputs, and routes models.',
    migration: 'Use Cursor for local development and app.nz for cloud agent tasks, deploys, previews, and model routing.',
  },
  {
    slug: 'devin',
    competitor: 'Devin',
    category: 'agent-cloud',
    url: 'https://devin.ai',
    summary: 'Devin is an AI software engineer product that runs delegated coding tasks on an autonomous agent.',
    strengths: ['Autonomous software-engineer product.', 'Teams experimenting with delegated coding tasks.', 'Focused on agent task execution.'],
    chooseThem: 'Choose Devin for a focused autonomous software-engineering teammate.',
    appNzAngle: 'app.nz treats coding agents as one part of the cloud: after the agent edits code, the platform builds, deploys, routes models, and hosts GPU endpoints.',
    migration: 'Use app.nz when agent work should connect directly to deploys, model gateway calls, and hosted sites.',
  },
  {
    slug: 'github-copilot',
    competitor: 'GitHub Copilot',
    category: 'agent-cloud',
    url: 'https://github.com/features/copilot',
    summary: 'GitHub Copilot integrates with GitHub and editors for code assistance and agent-style tasks.',
    strengths: ['GitHub and editor integration.', 'Fits teams standardized on GitHub.', 'Everyday coding assistance and automation.'],
    chooseThem: 'Choose GitHub Copilot when your team wants AI coding directly inside GitHub and supported editors.',
    appNzAngle: 'app.nz goes beyond code assistance: hosted repos, agents, deploys, model gateway, GPU endpoints, and queues.',
    migration: 'Keep Copilot for editor assistance and use app.nz for cloud agent runs, deployable artifacts, and model routing.',
  },
  {
    slug: 'replit-agent',
    competitor: 'Replit Agent',
    category: 'agent-cloud',
    url: 'https://replit.com',
    summary: "Replit Agent builds and iterates on apps inside Replit's browser-based environment.",
    strengths: ['Browser-native development environment.', 'Quick app creation and education workflows.', 'Coding, running, and sharing in one workspace.'],
    chooseThem: 'Choose Replit Agent when the whole coding experience should stay in Replit.',
    appNzAngle: 'app.nz is more infrastructure-oriented: agents, deploys, model gateway, GPU endpoints, queues, datasets, and hosting in one cloud.',
    migration: 'Move generated apps to app.nz when they need production hosting, model routing, or GPU inference.',
  },
  {
    slug: 'lovable',
    competitor: 'Lovable',
    category: 'agent-cloud',
    url: 'https://lovable.dev',
    summary: 'Lovable generates full-stack web apps from prompts, aimed at prototypes and product experiments.',
    strengths: ['Fast prompt-to-app workflow.', 'Nontraditional builders and product prototypes.', 'Quick UI and app exploration.'],
    chooseThem: 'Choose Lovable when the main goal is rapidly generating and iterating on a web app from prompts.',
    appNzAngle: 'app.nz is the next layer when the generated app needs agents, deploys, model routing, GPU endpoints, and queues.',
    migration: 'Export or connect the generated code, then use app.nz for hosting, agent maintenance, and model gateway calls.',
  },
  {
    slug: 'bolt-new',
    competitor: 'Bolt.new',
    category: 'agent-cloud',
    url: 'https://bolt.new',
    summary: 'Bolt.new generates apps in the browser for fast interactive prototyping.',
    strengths: ['Browser-based app generation.', 'Prototypes and frontend-heavy experiments.', 'Tight prompt, edit, preview loop.'],
    chooseThem: 'Choose Bolt.new when prompt-to-prototype speed in the browser is the main requirement.',
    appNzAngle: 'app.nz is broader than generation: it hosts the app, routes model calls, runs agents on repos, and provides GPU infrastructure.',
    migration: 'Move the app code or static build to app.nz when it needs durable hosting, model gateway integration, or GPU features.',
  },
  {
    slug: 'windsurf',
    competitor: 'Windsurf',
    category: 'agent-cloud',
    url: 'https://windsurf.com',
    summary: 'Windsurf is an AI coding environment for agentic development in the editor.',
    strengths: ['Editor-centered agentic coding.', 'AI assistance inside the coding environment.', 'Codebase-aware edits and iteration.'],
    chooseThem: 'Choose Windsurf when you need an AI coding environment for day-to-day development.',
    appNzAngle: 'app.nz starts where editor agents leave off: cloud agent runs, deploys, model routing, GPU hosting, and queues.',
    migration: 'Use Windsurf for coding and app.nz for cloud execution, deploy previews, and model gateway calls.',
  },
];

export const alternatives: Alternative[] = seeds.map((seed) => ({
  ...seed,
  title: `${seed.competitor} Alternative: app.nz`,
  categoryLabel: alternativeCategoryLabels[seed.category],
  excerpt: `${seed.summary} app.nz provides agents, hosting, deploys, model routing, and GPU infrastructure.`,
  readTime: seed.category === 'cloud-hosting' ? '6 min read' : '5 min read',
  body: articleBody(seed),
}));

export const alternativeSlugs = alternatives.map((alt) => alt.slug);

export const alternativeBySlug: Record<string, Alternative> = Object.fromEntries(
  alternatives.map((alt) => [alt.slug, alt]),
);
